import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, BarChart, Bar,
} from "recharts";
import {
  Users, Download, Crown, TrendingUp, ArrowLeft, Loader2, Shield, Film, Music,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { formatSize, formatDate } from "@/lib/utils-vg";

const PIE_COLORS = ["#7C3AED", "#06B6D4", "#F59E0B", "#EC4899", "#10B981", "#6366F1", "#EF4444"];

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="vg-glass rounded-2xl p-5 relative overflow-hidden" data-testid={testid}>
    <div className={`absolute -top-10 -right-8 h-28 w-28 rounded-full blur-3xl ${accent}`} />
    <div className="flex items-center justify-between relative">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-slate-500">{label}</p>
        <p className="text-3xl font-head font-extrabold mt-1">{value}</p>
      </div>
      <div className="h-11 w-11 rounded-xl bg-white/5 border border-white/10 grid place-items-center">
        <Icon size={20} className="text-violet-300" />
      </div>
    </div>
  </div>
);

const ChartCard = ({ title, children, testid }) => (
  <div className="vg-glass rounded-2xl p-5" data-testid={testid}>
    <h3 className="font-head font-semibold text-white mb-4">{title}</h3>
    {children}
  </div>
);

const tooltipStyle = {
  background: "#12141F",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 12,
  color: "#fff",
  fontSize: 12,
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, u, d] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/downloads"),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setDownloads(d.data);
    } catch {
      toast.error(t("admin.loadErr"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (user === null) return; // still checking
    if (!user || user.role !== "admin") {
      navigate("/");
      return;
    }
    load();
  }, [user, navigate, load]);

  const togglePremium = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/premium`, { grant: !u.is_premium, days: 30 });
      toast.success(u.is_premium ? t("admin.premRemoved") : t("admin.granted"));
      load();
    } catch {
      toast.error(t("admin.actionFail"));
    }
  };

  if (loading || !stats) {
    return (
      <div className="min-h-screen vg-radial grid place-items-center">
        <Loader2 size={32} className="vg-spin text-violet-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen vg-radial" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#090A0F]/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 grid place-items-center">
              <Shield size={18} className="text-white" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-lg font-head font-bold">{t("admin.title")}</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t("admin.subtitle")}</span>
            </div>
          </div>
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 px-4 h-9 rounded-xl bg-white/5 border border-white/10 text-sm text-slate-200 hover:bg-white/10 transition-colors"
            data-testid="admin-back-button"
          >
            <ArrowLeft size={15} /> {t("admin.back")}
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8 space-y-6">
        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} label={t("admin.totalUsers")} value={stats.total_users} accent="bg-violet-600/30" testid="stat-total-users" />
          <StatCard icon={Download} label={t("admin.totalDownloads")} value={stats.total_downloads} accent="bg-cyan-500/30" testid="stat-total-downloads" />
          <StatCard icon={Crown} label={t("admin.premiumUsers")} value={stats.premium_users} accent="bg-amber-500/30" testid="stat-premium-users" />
          <StatCard icon={TrendingUp} label={t("admin.downloadsToday")} value={stats.downloads_today} accent="bg-emerald-500/30" testid="stat-downloads-today" />
        </div>

        {/* Line charts */}
        <div className="grid lg:grid-cols-2 gap-4">
          <ChartCard title={t("admin.dlChart")} testid="chart-downloads">
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={stats.downloads_series}>
                <defs>
                  <linearGradient id="dlGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#7C3AED" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="count" stroke="#a78bfa" strokeWidth={2} fill="url(#dlGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title={t("admin.suChart")} testid="chart-signups">
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={stats.signups_series}>
                <defs>
                  <linearGradient id="suGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06B6D4" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#06B6D4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="count" stroke="#22d3ee" strokeWidth={2} fill="url(#suGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* Breakdowns */}
        <div className="grid lg:grid-cols-2 gap-4">
          <ChartCard title={t("admin.byPlatform")} testid="chart-platforms">
            {stats.platform_breakdown.length === 0 ? (
              <p className="text-sm text-slate-500 py-16 text-center">{t("admin.noData")}</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={stats.platform_breakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                    {stats.platform_breakdown.map((entry, i) => (
                      <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          <ChartCard title={t("admin.byQuality")} testid="chart-quality">
            {stats.quality_breakdown.length === 0 ? (
              <p className="text-sm text-slate-500 py-16 text-center">{t("admin.noData")}</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stats.quality_breakdown}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={11} />
                  <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {stats.quality_breakdown.map((entry, i) => (
                      <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>

        {/* Users table */}
        <div className="vg-glass rounded-2xl p-5" data-testid="admin-users-table">
          <h3 className="font-head font-semibold text-white mb-4">{t("admin.users")} ({users.length})</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 text-xs font-mono uppercase tracking-wider border-b border-white/10">
                  <th className="pb-3 pr-4">{t("admin.colUser")}</th>
                  <th className="pb-3 pr-4">{t("admin.colDownloads")}</th>
                  <th className="pb-3 pr-4">{t("admin.colJoined")}</th>
                  <th className="pb-3 pr-4">{t("admin.colStatus")}</th>
                  <th className="pb-3 text-right">{t("admin.colAction")}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid="admin-user-row">
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 grid place-items-center text-xs font-bold uppercase shrink-0">
                          {(u.name || u.email).charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-white truncate">{u.name || "—"}</p>
                          <p className="text-xs text-slate-500 truncate">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-4 font-mono">{u.download_count}</td>
                    <td className="py-3 pr-4 text-slate-400">{formatDate(u.created_at)}</td>
                    <td className="py-3 pr-4">
                      {u.role === "admin" ? (
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20">
                          <Shield size={11} /> {t("admin.roleAdmin")}
                        </span>
                      ) : u.is_premium ? (
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/20">
                          <Crown size={11} /> {t("admin.premium")}
                        </span>
                      ) : (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400 border border-white/10">
                          {t("admin.free")}
                        </span>
                      )}
                    </td>
                    <td className="py-3 text-right">
                      {u.role !== "admin" && (
                        <button
                          onClick={() => togglePremium(u)}
                          className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                            u.is_premium
                              ? "border-rose-500/30 text-rose-400 bg-rose-500/10 hover:bg-rose-500/20"
                              : "border-violet-500/40 text-violet-300 bg-violet-500/10 hover:bg-violet-500/20"
                          }`}
                          data-testid="admin-toggle-premium"
                        >
                          {u.is_premium ? t("admin.remove") : t("admin.grant")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent downloads */}
        <div className="vg-glass rounded-2xl p-5" data-testid="admin-recent-downloads">
          <h3 className="font-head font-semibold text-white mb-4">{t("admin.recent")}</h3>
          {downloads.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">{t("admin.noDownloads")}</p>
          ) : (
            <div className="space-y-2">
              {downloads.slice(0, 20).map((d) => (
                <div key={d.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.02] border border-white/5">
                  <div className="h-10 w-14 rounded-lg overflow-hidden bg-black/40 border border-white/10 shrink-0">
                    {d.thumbnail ? (
                      <img src={d.thumbnail} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-slate-600">
                        {d.file_type === "audio" ? <Music size={14} /> : <Film size={14} />}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white truncate">{d.title}</p>
                    <p className="text-[11px] text-slate-500">
                      {d.user_email} · {d.platform} · {d.quality === "audio" ? "MP3" : d.quality === "best" ? "Best" : `${d.quality}p`}
                      {d.file_size ? ` · ${formatSize(d.file_size)}` : ""}
                    </p>
                  </div>
                  <span className="text-[11px] text-slate-600 shrink-0">{formatDate(d.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
